import { PlatformType, PublishDefaults } from "../models/Platform";
import { PlatformSpecificSettings } from "../models/Post";

type ConnectionSettings = PlatformSpecificSettings[keyof PlatformSpecificSettings];

/** Platforms whose publisher posts a first comment (server postFirstComment). */
export const FIRST_COMMENT_PLATFORMS: ReadonlySet<PlatformType> = new Set([
  "instagram",
  "linkedin",
  "facebook",
  "youtube",
]);

/**
 * The settings a channel remembers from its last post, per platform. Mirrors
 * server/src/services/publishDefaults.ts, which writes them on schedule.
 */
const REMEMBERED_KEYS: Record<PlatformType, (keyof PublishDefaults)[]> = {
  instagram: ["videoEnabled", "postType", "firstCommentEnabled"],
  facebook: ["videoEnabled", "postType", "firstCommentEnabled"],
  linkedin: ["videoEnabled", "firstCommentEnabled"],
  youtube: ["privacy", "madeForKids", "firstCommentEnabled"],
  pinterest: ["boardId", "boardName"],
  tiktok: ["videoEnabled"],
};

/**
 * Settings for a channel newly added to a post: the platform defaults, then
 * whatever this channel used last time. First comment starts off on platforms
 * that support it, so a channel only comments once someone turns it on.
 */
export function initialConnectionSettings(
  platform: PlatformType,
  platformDefaults: ConnectionSettings,
  remembered: PublishDefaults | undefined
): ConnectionSettings {
  const settings: Record<string, unknown> = { ...platformDefaults };
  if (FIRST_COMMENT_PLATFORMS.has(platform)) settings.firstCommentEnabled = false;
  for (const key of REMEMBERED_KEYS[platform] ?? []) {
    const value = remembered?.[key];
    if (value !== undefined) settings[key] = value;
  }
  return settings as ConnectionSettings;
}
